import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/shared/utils/shadcn";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-blue-500 text-white hover:bg-blue-500/90",
        destructive: "bg-red-500 text-white hover:bg-red-500/90",
        passive:
          "bg-grey-100 text-grey-700 hover:bg-white border border-grey-100 hover:border-grey-300",
        outline: "border border-neutral-200 bg-white hover:bg-neutral-100",
        secondary: "bg-blue-100 text-blue-500 hover:bg-blue-100/80",
        ghost: "hover:bg-neutral-100 border-none shadow-none",
        link: "text-primary underline-offset-4 hover:underline px-0 border-none shadow-none",
        round: "rounded-full hover:bg-neutral-100 px-0",
      },
      size: {
        default: "h-10 px-4",
        sm: "h-7 rounded-md px-3",
        md: "h-9 rounded-md px-4",
        lg: "h-11 rounded-md px-8",
        xl: "h-14 rounded-md px-10",
      },
    },
    compoundVariants: [
      {
        variant: "link",
        size: "default",
        className: "h-6 px-0 py-0",
      },
      {
        variant: "round",
        size: "default",
        className: "h-10 w-10 px-0 py-0",
      },
      {
        variant: "round",
        size: "sm",
        className: "h-7 w-7 px-0 py-0",
      },
      {
        variant: "round",
        size: "lg",
        className: "h-11 w-11 px-0 py-0",
      },
      {
        variant: "round",
        size: "xl",
        className: "h-14 w-14 px-0 py-0",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  label?: string;
  href?: string;
}

export type ButtonVariant = VariantProps<typeof buttonVariants>["variant"];

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      href,
      label,
      children,
      ...props
    },
    ref
  ) => {
    if (href) {
      return (
        <a
          className={cn(buttonVariants({ variant, size, className }))}
          href={href}
          {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {label || children}
        </a>
      );
    }

    const Component = asChild ? Slot : "button";
    return (
      <Component
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {label || children}
      </Component>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
